import { reCaptchaIntegrationEnabled } from "@config/site/site-config";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";

declare const grecaptcha;

const LOAD_TIMEOUT_MS = 10_000;

let loadPromise: Promise<void> | null = null;

const loadRecaptchaScript = (): Promise<void> => {
    if (typeof window === "undefined") {
        return Promise.reject(new Error("reCAPTCHA can only be loaded in the browser"));
    }
    if (typeof grecaptcha !== "undefined" && grecaptcha?.enterprise) {
        return Promise.resolve();
    }
    if (loadPromise) return loadPromise;

    loadPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_TOKEN}`;
        script.async = true;

        const timeoutId = setTimeout(() => {
            script.remove();
            loadPromise = null;
            reject(new Error(`reCAPTCHA script load timed out after ${LOAD_TIMEOUT_MS}ms`));
        }, LOAD_TIMEOUT_MS);

        script.onload = () => {
            clearTimeout(timeoutId);
            resolve();
        };
        script.onerror = () => {
            clearTimeout(timeoutId);
            script.remove();
            loadPromise = null;
            reject(new Error("Failed to load reCAPTCHA script"));
        };
        document.head.appendChild(script);
    });

    return loadPromise;
};

export const prefetchRecaptcha = (): void => {
    if (!reCaptchaIntegrationEnabled || !process.env.NEXT_PUBLIC_RECAPTCHA_TOKEN) return;
    loadRecaptchaScript().catch(() => {
        // Swallow — a real submit will retry and surface the error via generateRecaptchaToken.
    });
};

export const generateRecaptchaToken = async (action: string): Promise<string | null> => {
    if (!reCaptchaIntegrationEnabled || !process.env.NEXT_PUBLIC_RECAPTCHA_TOKEN) {
        return null;
    }

    try {
        await loadRecaptchaScript();

        await new Promise<void>((resolve) => {
            grecaptcha.enterprise.ready(() => {
                resolve();
            });
        });

        return await grecaptcha.enterprise.execute(process.env.NEXT_PUBLIC_RECAPTCHA_TOKEN, {
            action,
        });
    } catch (error) {
        Logger.error(ServiceType.RECAPTCHA, "Error occurred while executing reCAPTCHA", {
            tag: LogTag.RECAPTCHA,
            error,
        });
        return null;
    }
};
